import React from 'react';
import noop from 'lodash/noop';
import isEqual from 'lodash/isEqual';
import createAction, { IAction } from './createAction';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { AlertManager } from '@dealroadshow/uikit-alert';

type AllOrNone<T> = T | { [K in keyof T]?: never };
export type TDefaultMethod = (...args: any) => Promise<any>;
export type TActionTypes = Array<string | null>;
export type IReactReducerDispatch = React.Dispatch<IAction>;
export type TCallbacks<TMethod extends TDefaultMethod> = Parameters<typeof createAsyncRequest<TMethod>>[0]['callbacks'];
export type IAsyncRequest<TMethod extends TDefaultMethod> = (payload?: Parameters<TMethod>[0]) => Promise<void>;

export const createAsyncRequest = <TMethod extends TDefaultMethod>(
  {
    method,
    dispatch,
    types: [
      REQUEST_TYPE,
      SUCCESS_TYPE,
      ERROR_TYPE,
    ] = [],
    callbacks: {
      onSuccess = noop,
      onError = noop,
    } = {},
  }: {
    method: TMethod,
    callbacks?: {
      onSuccess?: (payload: {
        response: Awaited<ReturnType<TMethod>>,
        payload: Parameters<TMethod>[0],
        dispatch?: IReactReducerDispatch,
      }) => void,
      onError?: (payload: {
        error: unknown,
        payload: Parameters<TMethod>[0],
        dispatch?: IReactReducerDispatch,
      }) => void,
    },
  } & AllOrNone<{
    dispatch: IReactReducerDispatch,
    types: TActionTypes, // 0 - Request, 1 - Success, 2 - Error
  }>,
): IAsyncRequest<TMethod> => async (payload) => {
  if (dispatch && REQUEST_TYPE) {
    dispatch(createAction(REQUEST_TYPE));
  }

  try {
    const response = await method(payload);

    if (!isEqual(onSuccess, noop)) {
      onSuccess({ response, payload, dispatch });
    }

    if (dispatch && SUCCESS_TYPE) {
      dispatch(createAction(SUCCESS_TYPE, response));
    }
  } catch (error) {
    if (!isEqual(onError, noop)) {
      onError({ error, payload, dispatch });
    } else {
      AlertManager.error(getErrorMessage(error));
    }
    if (dispatch && ERROR_TYPE) {
      dispatch(createAction(ERROR_TYPE, error));
    }
  }
};

export default createAsyncRequest;
